/**
 * ScnSpecDetailPage (S7.2) — SCN 共同規格主檔 detail (交付稿 board A3), in the
 * category workflow-node visual style: `useElevation().card` resting cards,
 * `PurpleTag` identity chip, `token.*` only, zh-primary naming.
 *
 * READ-ONLY: spec metadata comes from the one-time seed, membership is always
 * derived from the products collection (`GET /scn-groups/:scn`, S7.1) — no
 * edit affordances exist here (hide-don't-disable; spec-metadata authoring is
 * out of scope until a board cites it). Members-only groups (no seeded spec
 * yet, e.g. SCN-006) render dash spec fields; a 0-member group stays listed
 * as 未啟用 with an explicit empty members state (board A2 0-SKU rule).
 *
 * Members table reuses the ScnSection member-column patterns (SKU +
 * internal-ID two-line cell, zh-secondary display name, status tag) with
 * row-click navigation to the product detail — the same read-back the product
 * side renders, so the relation is visible from both ends.
 */

import { useNavigate, useParams } from 'react-router-dom';
import { Breadcrumb, Button, Card, Divider, Skeleton, Table, Typography, theme } from 'antd';
import type { TableProps } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { useRequest } from 'ahooks';
import PurpleTag from '@/components/common/PurpleTag';
import StatusTag from '@/components/common/StatusTag';
import TableContainer, { ListEmptyState, ListErrorState, renderDash } from '@/components/common/TableContainer';
import { productApi } from '@/services/api';
import { marketOfSubsidiary } from '@/lib/scn';
import type { ScnSpecGroup } from '@/types/api/scn';
import type { Product } from '@/types/api/product';
import { formatDate } from '@/utils/dateUtils';
import { useElevation } from '@/styles/useElevation';
import PageContainer from '@/components/common/PageContainer';

const { Text, Title } = Typography;

const ScnSpecDetailPage = () => {
  const { scn } = useParams<{ scn: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(['scn', 'products', 'common']);
  const { token } = theme.useToken();
  const elevation = useElevation();
  const isZh = i18n.language.startsWith('zh');

  // Through the facade, not around it. `productApi.scnGroupSpec` wraps this
  // EXACT request and is already a §0g named hole with a removal trigger — so
  // the direct call here was a bypass of a route that exists, the one class
  // the round-3 panel did not name. When a `scnGroup(scn)` root lands and the
  // method flips, this page flips with it instead of silently staying on MSW.
  // `scn` is `ready`-gated below, so the coalesce never reaches the network.
  const { data, error, loading, run } = useRequest(
    async () => (await productApi.scnGroupSpec(scn ?? '')).data,
    {
      ready: !!scn,
      refreshDeps: [scn],
      cacheKey: scn ? `/api/scn-groups/${scn}` : undefined,
    }
  );

  const group: ScnSpecGroup | undefined = data;
  const notFound = error instanceof AxiosError && error.response?.status === 404;

  /** Back + breadcrumb row (主資料 › SCN 共同規格主檔 › code). */
  const backRow = (
    <div className="tw-flex tw-items-center tw-gap-2 tw-mb-4">
      <Button
        type="link"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate('/auth/products/scn')}
        style={{ padding: 0, height: 'auto', lineHeight: 1 }}
      >
        {t('scn:detail.backToList')}
      </Button>
      <Breadcrumb
        items={[
          { title: t('common:masterData') },
          {
            title: (
              <a onClick={() => navigate('/auth/products/scn')}>{t('scn:menu.specMaster')}</a>
            ),
          },
          { title: scn ?? '' },
        ]}
      />
    </div>
  );

  if (loading || (!group && !error)) {
    return (
      <PageContainer>
        <Card className="tw-mb-6" style={{ ...elevation.card }}>
          {backRow}
          <Skeleton active paragraph={{ rows: 4 }} />
        </Card>
      </PageContainer>
    );
  }

  if (notFound || !group) {
    return (
      <PageContainer>
        <Card className="tw-mb-6" style={{ ...elevation.card }}>
          {backRow}
          {notFound ? (
            <ListEmptyState
              title={t('scn:detail.states.notFoundTitle')}
              description={t('scn:detail.states.notFoundDescription')}
              actionLabel={t('scn:detail.backToList')}
              onAction={() => navigate('/auth/products/scn')}
              actionType="default"
              actionIcon={<ArrowLeftOutlined />}
            />
          ) : (
            <ListErrorState
              title={t('scn:detail.states.loadFailed')}
              retryLabel={t('common:errors.boundary.retry')}
              onRetry={run}
            />
          )}
        </Card>
      </PageContainer>
    );
  }

  // zh-primary naming: zh mirror first under zh locale, EN mirror secondary.
  const primaryName = (isZh ? (group.specNameZh ?? group.specName) : group.specName) ?? group.scn;
  const secondaryName = isZh
    ? group.specNameZh
      ? group.specName
      : null
    : group.specNameZh;

  const memberColumns: TableProps<Product>['columns'] = [
    {
      title: t('products:fields.sku'),
      dataIndex: 'sku',
      key: 'sku',
      width: 200,
      render: (sku: string, record) => (
        <div className="tw-flex tw-flex-col tw-gap-0.5">
          <span style={{ fontWeight: token.fontWeightStrong }}>{sku}</span>
          <span style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}>
            {record.internalProductId}
          </span>
        </div>
      ),
    },
    {
      title: t('products:fields.displayName'),
      dataIndex: 'displayName',
      key: 'displayName',
      render: (value: string, record) => (
        <div className="tw-flex tw-flex-col tw-gap-0.5">
          <span>{value}</span>
          {record.nameZh && (
            <span style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}>
              {record.nameZh}
            </span>
          )}
        </div>
      ),
    },
    {
      title: t('products:fields.oemNo'),
      dataIndex: 'oemNo',
      key: 'oemNo',
      width: 160,
      render: (value: string | null | undefined) => renderDash(value),
    },
    {
      title: t('scn:detail.fields.market'),
      dataIndex: 'subsidiaryId',
      key: 'market',
      width: 110,
      render: (subsidiaryId: string) => marketOfSubsidiary(subsidiaryId),
    },
    {
      title: t('common:status.title'),
      dataIndex: 'isActive',
      key: 'isActive',
      width: 120,
      render: (isActive: boolean) => (
        <StatusTag
          status={isActive ? 'active' : 'inactive'}
          label={t(isActive ? 'common:status.active' : 'common:status.inactive')}
        />
      ),
    },
  ];

  /** Labeled read-only field (workflow-node detail row). */
  const specField = (label: string, value: React.ReactNode) => (
    <div className="tw-flex tw-flex-col tw-gap-1">
      <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
        {label}
      </Text>
      <span style={{ fontSize: token.fontSize }}>{renderDash(value)}</span>
    </div>
  );

  return (
    <PageContainer>
      {/* Header card */}
      <Card className="tw-mb-6" style={{ ...elevation.card }}>
        {backRow}
        <Divider style={{ margin: '12px 0' }} />
        <div className="tw-flex tw-items-center tw-gap-3 tw-flex-wrap">
          <h1
            style={{
              fontSize: token.fontSizeHeading3,
              fontWeight: token.fontWeightStrong,
              color: token.colorPrimary,
              margin: 0,
              lineHeight: 1.2,
            }}
          >
            {primaryName}
          </h1>
          <PurpleTag style={{ fontSize: token.fontSize, padding: '2px 12px', margin: 0 }}>
            {group.scn}
          </PurpleTag>
          <StatusTag
            status={group.status === 'in_use' ? 'active' : 'inactive'}
            label={t(
              group.status === 'in_use' ? 'scn:detail.status.inUse' : 'scn:detail.status.inactive'
            )}
          />
        </div>
        {secondaryName && (
          <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
            {secondaryName}
          </Text>
        )}
        <div className="tw-flex tw-items-center tw-gap-6 tw-mt-4 tw-flex-wrap">
          <div className="tw-flex tw-items-center tw-gap-2">
            <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
              {t('scn:detail.fields.memberCount')}
            </Text>
            <span style={{ fontWeight: token.fontWeightStrong }}>{group.memberCount}</span>
          </div>
          <div className="tw-flex tw-items-center tw-gap-2">
            <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
              {t('scn:detail.fields.markets')}
            </Text>
            <span>{group.markets.length ? group.markets.join(' / ') : renderDash(undefined)}</span>
          </div>
          <div className="tw-flex tw-items-center tw-gap-2">
            <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
              {t('scn:detail.fields.updatedAt')}
            </Text>
            <span>{formatDate(group.updatedAt)}</span>
          </div>
        </div>
      </Card>

      {/* 規格基本屬性 — group-level spec fields (board A3), read-only */}
      <Card className="tw-mb-6" style={{ ...elevation.card }}>
        <div className="tw-mb-4">
          <Title level={4} style={{ margin: 0 }}>
            {t('scn:detail.specSection')}
          </Title>
          <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
            {t('scn:detail.specSectionNote')}
          </Text>
        </div>
        <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-6">
          {specField(
            t('scn:detail.fields.scnCode'),
            <PurpleTag style={{ margin: 0 }}>{group.scn}</PurpleTag>
          )}
          {specField(t('scn:detail.fields.majorCategory'), group.majorCategory)}
          {specField(
            t('scn:detail.fields.specName'),
            group.specName || group.specNameZh ? (
              <span>
                {primaryName}
                {secondaryName && (
                  <Text
                    type="secondary"
                    style={{ fontSize: token.fontSizeSM, marginLeft: token.marginXS }}
                  >
                    {secondaryName}
                  </Text>
                )}
              </span>
            ) : null
          )}
          <div className="md:tw-col-span-2">
            {specField(t('scn:detail.fields.specDescription'), group.specDescription)}
          </div>
        </div>
      </Card>

      {/* 關聯商品 — membership derived from products; row-click drills in */}
      <Card style={{ ...elevation.card }}>
        <Title level={4} style={{ marginTop: 0, marginBottom: token.marginMD }}>
          {t('scn:detail.membersSection')}
        </Title>
        <TableContainer>
          <Table<Product>
            rowKey="id"
            size="small"
            dataSource={group.members}
            columns={memberColumns}
            pagination={false}
            rowClassName={(record) => (!record.isActive ? 'inactive-row' : '')}
            onRow={(record) => ({
              onClick: () => navigate(`/auth/products/${record.id}`),
              style: { cursor: 'pointer' },
            })}
            locale={{
              emptyText: (
                <ListEmptyState
                  title={t('scn:detail.membersEmptyTitle')}
                  description={t('scn:detail.membersEmptyDescription')}
                />
              ),
            }}
            scroll={{ x: 800 }}
          />
        </TableContainer>
      </Card>
    </PageContainer>
  );
};

export default ScnSpecDetailPage;
